import { Poly } from "../poly";
import skillAffectTable from "./skillaffect.json";

/** One `TAffectData`: a printf sentence, and the formulas for its first and second value. */
export type SkillAffect = readonly [description: string, min: string, max: string];

/** The `POINT_*` values a skill formula may name, as `CPythonPlayer::GetStatus` holds them. */
export interface SkillStatus {
  readonly level: number;
  readonly st: number;
  readonly dx: number;
  readonly iq: number;
  readonly ht: number;
  readonly maxHp: number;
  readonly maxSp: number;
  readonly defGrade: number;
  readonly attSpeed: number;
  readonly minAtk: number;
  readonly maxAtk: number;
  readonly minWep: number;
  readonly maxWep: number;
  readonly minMagicWep: number;
  readonly maxMagicWep: number;
  readonly magicAttGrade: number;
}

export const NO_SKILL_STATUS: SkillStatus = {
  level: 0,
  st: 0,
  dx: 0,
  iq: 0,
  ht: 0,
  maxHp: 0,
  maxSp: 0,
  defGrade: 0,
  attSpeed: 0,
  minAtk: 0,
  maxAtk: 0,
  minWep: 0,
  maxWep: 0,
  minMagicWep: 0,
  maxMagicWep: 0,
  magicAttGrade: 0,
};

/** The standing fields a skill formula reads, in the client's spelling. */
export interface StandingForSkills {
  readonly level: number;
  readonly str: number;
  readonly dex: number;
  readonly int: number;
  readonly con: number;
  readonly maxHp: number;
  readonly attackMin: number;
  readonly attackMax: number;
  readonly defence: number;
  readonly attackSpeed: number;
  readonly weaponMin: number;
  readonly weaponMax: number;
  readonly weaponMagicMin: number;
  readonly weaponMagicMax: number;
}

/**
 * The status a skill tooltip evaluates against: the sheet's own figures, with
 * `ATT_MIN`/`ATT_MAX` the attack pair the character window prints
 * (`uicharacter.py:468`), and the worn weapon's four dice `__SetWeaponPower`
 * sets (`PythonPlayer.cpp:418-421`). The magic attack grade reaches no frame, so
 * the names that read it evaluate to 0.
 */
export function skillStatusOf(standing: StandingForSkills, maxSp: number): SkillStatus {
  return {
    ...NO_SKILL_STATUS,
    level: standing.level,
    st: standing.str,
    dx: standing.dex,
    iq: standing.int,
    ht: standing.con,
    maxHp: standing.maxHp,
    maxSp,
    defGrade: standing.defence,
    attSpeed: standing.attackSpeed,
    minAtk: standing.attackMin,
    maxAtk: standing.attackMax,
    minWep: standing.weaponMin,
    maxWep: standing.weaponMax,
    minMagicWep: standing.weaponMagicMin,
    maxMagicWep: standing.weaponMagicMax,
  };
}

type Point = keyof SkillStatus | "hitRate" | "none";

/** `ms_StatusNameMap`: which point each formula name reads. */
const STATUS_NAMES: Readonly<Record<string, Point>> = {
  chain: "none", // PythonSkill.cpp:964
  HR: "hitRate", // PythonSkill.cpp:965
  LV: "level", // PythonSkill.cpp:966
  Level: "level", // PythonSkill.cpp:967
  MaxHP: "maxHp", // PythonSkill.cpp:968
  MaxSP: "maxSp", // PythonSkill.cpp:969
  MinMWEP: "minWep", // PythonSkill.cpp:970
  MaxMWEP: "maxWep", // PythonSkill.cpp:971
  MinWEP: "minWep", // PythonSkill.cpp:972
  MaxWEP: "maxWep", // PythonSkill.cpp:973
  MinATK: "minAtk", // PythonSkill.cpp:974
  MaxATK: "maxAtk", // PythonSkill.cpp:975
  ATKSPD: "attSpeed", // PythonSkill.cpp:976
  AttackPower: "minAtk", // PythonSkill.cpp:977
  AtkMin: "minAtk", // PythonSkill.cpp:978
  AtkMax: "maxAtk", // PythonSkill.cpp:979
  DefencePower: "defGrade", // PythonSkill.cpp:980
  DEF: "defGrade", // PythonSkill.cpp:981
  MWEP: "magicAttGrade", // PythonSkill.cpp:982
  MagicAttackPower: "magicAttGrade", // PythonSkill.cpp:984
  INT: "iq", // PythonSkill.cpp:985
  STR: "st", // PythonSkill.cpp:986
  DEX: "dx", // PythonSkill.cpp:987
  CON: "ht", // PythonSkill.cpp:988
  minatk: "minAtk", // PythonSkill.cpp:990
  maxatk: "maxAtk", // PythonSkill.cpp:991
  minmtk: "minWep", // PythonSkill.cpp:992
  maxmtk: "maxWep", // PythonSkill.cpp:993
  maxhp: "maxHp", // PythonSkill.cpp:996
  maxsp: "maxSp", // PythonSkill.cpp:997
  odef: "defGrade", // PythonSkill.cpp:998
  minwep: "minWep", // PythonSkill.cpp:1001
  maxwep: "maxWep", // PythonSkill.cpp:1002
  minmwep: "minMagicWep", // PythonSkill.cpp:1003
  maxmwep: "maxMagicWep", // PythonSkill.cpp:1004
  lv: "level", // PythonSkill.cpp:1005
  ar: "hitRate", // PythonSkill.cpp:1006
  iq: "iq", // PythonSkill.cpp:1007
  str: "st", // PythonSkill.cpp:1008
  dex: "dx", // PythonSkill.cpp:1009
  con: "ht", // PythonSkill.cpp:1010
};

/** `LocaleService_GetSkillPower`'s international table; a level past its end has no power. */
const SKILL_POWERS: readonly number[] = [
  0, // Locale.cpp:143
  5, 6, 8, 10, 12, // Locale.cpp:144
  14, 16, 18, 20, 22, // Locale.cpp:145
  24, 26, 28, 30, 32, // Locale.cpp:146
  34, 36, 38, 40, 50, // Locale.cpp:147
  52, 54, 56, 58, 60, // Locale.cpp:148
  63, 66, 69, 72, 82, // Locale.cpp:149
  85, 88, 91, 94, 98, // Locale.cpp:150
  102, 106, 110, 115, 125, // Locale.cpp:151
  125, // Locale.cpp:152
];

/** `fcurEfficientPercentage` for a skill at `level`: its power over a hundred, as a C float. */
export function skillPercentage(level: number): number {
  return Math.fround((SKILL_POWERS[level] ?? 0) / 100); // PythonPlayer.cpp:1033
}

/** `__GetHitRate`: the `ar` a formula reads, from dexterity and level. */
export function hitRate(status: SkillStatus): number {
  const src = Math.trunc((status.dx * 4 + status.level * 2) / 6); // PythonPlayer.cpp:402
  return Math.trunc((100 * (Math.min(90, src) + 210)) / 300); // PythonPlayer.cpp:405
}

/** `GetState`: a name the map does not know, `JeungJi` included, reads 0. */
function statusValue(name: string, status: SkillStatus): number {
  const point = STATUS_NAMES[name];
  if (point === undefined || point === "none") return 0;
  return point === "hitRate" ? hitRate(status) : status[point];
}

/** `ProcessFormula`: `SkillPoint` and `k` read the skill's power, every other name the player's status. */
export function processFormula(formula: string, skillPercent: number, status: SkillStatus): number {
  const poly = new Poly(formula);
  if (poly.analyze()) {
    for (const name of poly.varNames()) {
      if (name === "SkillPoint" || name === "k") {
        poly.setVar(name, skillPercent); // PythonSkill.cpp:1258-1260
        continue;
      }
      const state = statusValue(name, status);
      poly.setVar(name, name === "ar" ? Math.fround(state / 100) : state); // PythonSkill.cpp:1270-1271
    }
  }
  return poly.evaluate(); // PythonSkill.cpp:1282
}

/** `_snprintf` over the conversions a shipped affect sentence uses: `%%` and `%[flags][width][.precision]f`. */
function formatAffect(format: string, values: readonly number[]): string {
  let next = 0;
  return format.replace(/%%|%[-+ #0]*\d*(?:\.(\d*))?f/g, (spec: string, precision: string | undefined) => {
    if (spec === "%%") return "%";
    const value = values[next++] ?? 0;
    return value.toFixed(precision === undefined ? 6 : Number(precision || "0"));
  });
}

/** `GetAffectDescription`: one sentence with both values unsigned, floored when it prints `%.0f`. */
export function affectDescription(affect: SkillAffect, skillPercent: number, status: SkillStatus): string {
  const [description, minFormula, maxFormula] = affect;
  let min = Math.abs(processFormula(minFormula, skillPercent, status)); // PythonSkill.cpp:1300,1303-1304
  let max = Math.abs(processFormula(maxFormula, skillPercent, status)); // PythonSkill.cpp:1301,1305-1306
  if (description.includes("%.0f")) {
    min = Math.floor(min); // PythonSkill.cpp:1338
    max = Math.floor(max); // PythonSkill.cpp:1339
  }
  return formatAffect(description, [min, max]); // PythonSkill.cpp:1343
}

/** `AffectDataVector` for a skill, empty when it has none. */
export function skillAffectsFor(vnum: number): readonly SkillAffect[] {
  return (skillAffectTable as unknown as Record<string, SkillAffect[]>)[String(vnum)] ?? [];
}

/** `SKILL_GRADE_COUNT`: the perfect grade, which shows no level and no next level. */
const SKILL_GRADE_COUNT = 3; // PythonSkill.h:140

/**
 * `AppendSkillDataNew`'s level sections for a skill at overall `level`: the
 * grade-relative current level and each affect sentence at the current power
 * when learned, then the next level and its sentences until the grade's last
 * level. Empty for a skill with no affect sentence.
 */
export function skillAffectLines(vnum: number, level: number, status: SkillStatus): string[] {
  const affects = skillAffectsFor(vnum);
  if (affects.length === 0) return []; // uitooltip.py:2236
  const grade = level >= 40 ? 3 : level >= 30 ? 2 : level >= 20 ? 1 : 0;
  const relative = grade === 0 ? level : level - (grade * 10 + 10) + 1; // PythonPlayer.cpp:1002,1006,1010,1014
  const end = grade === 0 ? 20 : 10; // uitooltip.py:2105
  const lines: string[] = [];
  const sentences = (percent: number): void => {
    for (const affect of affects) lines.push(affectDescription(affect, percent, status)); // uitooltip.py:2170-2171
  };
  if (relative > 0) {
    // uitooltip.py:2113
    if (grade !== SKILL_GRADE_COUNT) {
      lines.push(relative === end ? `Current Level: ${relative} (Master)` : `Current Level: ${relative}`); // uitooltip.py:2118-2121
    }
    sentences(skillPercentage(level)); // uitooltip.py:2122
  }
  if (grade !== SKILL_GRADE_COUNT && relative < end) {
    // uitooltip.py:2125-2126
    lines.push(`Next Level: ${relative + 1} (Max. ${end})`); // uitooltip.py:2133
    sentences(skillPercentage(level + 1)); // uitooltip.py:2134
  }
  return lines;
}
